import { AnimatePresence, motion } from 'framer-motion';
import {
  Calendar,
  ChevronDown,
  Compass,
  PieChart,
  PiggyBank,
  Target,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState } from 'react';
import type { BudgetModel } from '../../../data/types';
import { cn } from '../../../lib/cn';
import { springDefault, springQuick, tapScale } from '../../../tokens';
import { StepLayout } from '../StepLayout';
import type { OnboardingDraft } from '../types';

type Props = {
  draft: OnboardingDraft;
  setDraft: (patch: Partial<OnboardingDraft>) => void;
  onContinue: () => void;
  canContinue: boolean;
};

interface ModelDef {
  value: BudgetModel;
  title: string;
  tagline: string;
  body: string;
  icon: LucideIcon;
}

const MODELS: ModelDef[] = [
  {
    value: 'daily',
    title: 'Daily Allowance',
    tagline: 'Know what you can spend today',
    body: 'Income minus bills, divided across the days in your pay period. The hero number on the home screen tells you exactly how much you can spend right now and stay on pace.',
    icon: Calendar,
  },
  {
    value: '503020',
    title: '50 / 30 / 20',
    tagline: 'Balance needs, wants, and savings',
    body: 'Half your income covers needs, 30% for the things you enjoy, 20% goes to savings. Each transaction lands in one of three buckets so you can see the balance at a glance.',
    icon: PieChart,
  },
  {
    value: 'pyf',
    title: 'Pay Yourself First',
    tagline: 'Save first, spend what’s left',
    body: 'Decide how much of every paycheck moves to savings before anything else, then spend the rest freely. You’ll see your hourly rate translated into daily, monthly, and yearly savings projections.',
    icon: PiggyBank,
  },
  {
    value: 'zero',
    title: 'Zero-Based',
    tagline: 'Give every dollar a job',
    body: 'Assign every dollar of your income to a category before the month begins. The home screen tracks how much is still unassigned and whether each category is on pace.',
    icon: Target,
  },
  {
    value: 'conscious',
    title: 'Conscious Spending',
    tagline: 'Spend on what matters',
    body: 'No allocations, no limits. Track every transaction and let the data show you where your money goes. Margin surfaces trends and outliers so you can spend lavishly on what you love and cut what you don’t.',
    icon: Compass,
  },
];

export function BudgetModelStep({
  draft,
  setDraft,
  onContinue,
  canContinue,
}: Props) {
  return (
    <StepLayout
      icon={<Target size={28} strokeWidth={1.5} />}
      title="Choose your budgeting style"
      description="Pick the one that matches how you want to think about money. You can change it later in Settings."
      onContinue={onContinue}
      canContinue={canContinue}
    >
      <div className="flex flex-col gap-card-gap">
        {MODELS.map((m) => (
          <ModelCard
            key={m.value}
            def={m}
            selected={draft.budgetModel === m.value}
            onSelect={() => setDraft({ budgetModel: m.value })}
          />
        ))}
      </div>
    </StepLayout>
  );
}

function ModelCard({
  def,
  selected,
  onSelect,
}: {
  def: ModelDef;
  selected: boolean;
  onSelect: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const Icon = def.icon;

  return (
    <div
      className={cn(
        'rounded-card overflow-hidden',
        selected ? 'bg-accent/10' : 'bg-surface',
        'shadow-md',
      )}
    >
      <motion.button
        type="button"
        onClick={onSelect}
        whileTap={tapScale}
        transition={springQuick}
        aria-pressed={selected}
        className="w-full p-card-pad flex items-start gap-3 text-left"
      >
        <div
          className={cn(
            'w-11 h-11 rounded-button shrink-0 inline-flex items-center justify-center',
            selected
              ? 'bg-accent text-white'
              : 'bg-surface-secondary text-text-secondary',
          )}
        >
          <Icon size={22} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-text-primary">
            {def.title}
          </div>
          <div className="text-[13px] text-text-secondary mt-0.5 leading-snug">
            {def.tagline}
          </div>
        </div>
      </motion.button>

      <motion.button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        whileTap={tapScale}
        transition={springQuick}
        aria-expanded={expanded}
        className={cn(
          'w-full px-card-pad py-2 -mt-1 flex items-center justify-between',
          'text-[13px] font-medium',
          selected ? 'text-accent' : 'text-text-secondary',
        )}
      >
        <span>{expanded ? 'Show less' : 'Learn more'}</span>
        <motion.span
          animate={{ rotate: expanded ? 180 : 0 }}
          transition={springQuick}
        >
          <ChevronDown size={16} strokeWidth={1.5} />
        </motion.span>
      </motion.button>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springDefault}
            className="overflow-hidden"
          >
            <p className="px-card-pad pb-card-pad text-[14px] leading-relaxed text-text-secondary">
              {def.body}
            </p>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
